/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/pages/AIProviders.tsx
 * DESCRIPTION: AI provider configuration and settings page for VeriMark.
 *              Manages multiple AI provider connections, API keys,
 *              usage analytics, and global settings with failover support.
 *              Admin-only access with tab-based provider organization.
 *
 * FEATURES:
 * - Admin-only access with authentication
 * - Tab-based provider organization (Mistral, Qwen, Alibaba)
 * - API key management with encryption
 * - Real-time connection testing
 * - Usage analytics and cost tracking
 * - Auto-failover configuration
 * - Rate limiting and timeout settings
 * - Dark/Light mode support
 *
 * LAYOUT STRUCTURE:
 * - Left Column: Provider Tabs + Provider Card + Usage Analytics
 * - Right Column: Global Settings + API Key Management
 *
 * DATA INTEGRATION:
 * - Mock data structured for easy API replacement
 * - All charts functional with tooltips
 * - Connection testing functional
 * - Production-ready structure
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 3.0.0
 * @created 2026-03-26
 * @updated 2026-03-27 - Complete rewrite with correct layout,
 *                       provider tabs, admin access, fixed time labels
 * ─────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect } from 'react';
import { useUser, useClerk } from '@clerk/react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  X,
  Eye,
  EyeOff,
  Star,
  Activity,
  AlertCircle,
  CheckCircle,
  Plus,
  Trash2,
  Copy,
  RefreshCw,
  Shield,
  Lock,
  Unlock,
  LogOut,
  TrendingUp,
  DollarSign,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

// ─────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────
interface Provider {
  id: string;
  name: string;
  displayName: string;
  description: string;
  logo: string;
  brandColor: string;
  status: 'available' | 'not-configured' | 'error' | 'testing';
  models: string[];
  currentModel: string;
  apiKey: string;
  isDefault: boolean;
  usage: {
    tokensUsed: number;
    requestsCount: number;
  };
  health: {
    responseTime: number;
    successRate: number;
    lastSuccessfulRequest: string;
    errorCount: number;
  };
}

interface APIKey {
  id: string;
  name: string;
  provider: string;
  created: string;
  lastUsed: string;
  status: 'active' | 'revoked';
  key: string;
}

interface GlobalSettings {
  defaultProvider: string;
  offlineMode: boolean;
  autoFailover: boolean;
  requestTimeout: number;
  maxRetryCount: number;
  rateLimit: number;
}

interface UsageData {
  time: string;
  mistral: number;
  qwen: number;
  openai: number;
}

interface TokenDistribution {
  name: string;
  value: number;
  color: string;
}

// ─────────────────────────────────────────────────────────────────────
// MOCK DATA
// ─────────────────────────────────────────────────────────────────────
const MOCK_PROVIDERS: Provider[] = [
  {
    id: '1',
    name: 'mistral',
    displayName: 'Mistral AI',
    description: 'Mistral AI is overall onsture alementation for oodt contents, and otechnical systems.',
    logo: 'M',
    brandColor: 'orange',
    status: 'available',
    models: ['Mistral 7B', 'Mistral 8x7B', 'Mistral Large'],
    currentModel: 'Mistral 7B',
    apiKey: 'sk-mistral-abc123...',
    isDefault: true,
    usage: {
      tokensUsed: 2840,
      requestsCount: 12,
    },
    health: {
      responseTime: 85,
      successRate: 99.8,
      lastSuccessfulRequest: '2026-03-28 15:32:01 GMT',
      errorCount: 0,
    },
  },
  {
    id: '2',
    name: 'qwen',
    displayName: 'Qwen/OpenAI',
    description: 'Qwen/OpenAI envastes air comnications and description for contents platform.',
    logo: 'Q',
    brandColor: 'purple',
    status: 'not-configured',
    models: ['Qwen-Chat', 'GPT-3.5-Turbo', 'GPT-4'],
    currentModel: 'Qwen-Chat',
    apiKey: '',
    isDefault: false,
    usage: {
      tokensUsed: 0,
      requestsCount: 0,
    },
    health: {
      responseTime: 0,
      successRate: 0,
      lastSuccessfulRequest: '-',
      errorCount: 0,
    },
  },
  {
    id: '3',
    name: 'alibaba',
    displayName: 'Alibaba',
    description: 'Alibaba colors, description &calibabat technologies.',
    logo: 'A',
    brandColor: 'orange',
    status: 'not-configured',
    models: ['Qwen-7B', 'Qwen-14B', 'Qwen-72B'],
    currentModel: 'Qwen-7B',
    apiKey: '',
    isDefault: false,
    usage: {
      tokensUsed: 0,
      requestsCount: 0,
    },
    health: {
      responseTime: 0,
      successRate: 0,
      lastSuccessfulRequest: '-',
      errorCount: 0,
    },
  },
];

const MOCK_API_KEYS: APIKey[] = [
  {
    id: '1',
    name: 'Production Key 1',
    provider: 'Mistral',
    created: '2026-03-28',
    lastUsed: '12:38 PM',
    status: 'active',
    key: 'sk-mistral-abc123...',
  },
  {
    id: '2',
    name: 'Production Key 2',
    provider: 'Mistral',
    created: '2026-03-28',
    lastUsed: '12:37 PM',
    status: 'active',
    key: 'sk-mistral-def456...',
  },
  {
    id: '3',
    name: 'Staging Key',
    provider: 'Mistral',
    created: '2026-03-28',
    lastUsed: '12:28 PM',
    status: 'active',
    key: 'sk-mistral-ghi789...',
  },
  {
    id: '4',
    name: 'Old Key',
    provider: 'OpenAI',
    created: '2026-03-28',
    lastUsed: '12:30 AM',
    status: 'revoked',
    key: 'sk-openai-jkl012...',
  },
];

const MOCK_USAGE_DATA: UsageData[] = [
  { time: '00:00', mistral: 200, qwen: 150, openai: 100 },
  { time: '04:00', mistral: 150, qwen: 120, openai: 80 },
  { time: '08:00', mistral: 380, qwen: 300, openai: 200 },
  { time: '12:00', mistral: 520, qwen: 420, openai: 320 },
  { time: '16:00', mistral: 450, qwen: 380, openai: 280 },
  { time: '20:00', mistral: 350, qwen: 280, openai: 220 },
  { time: '23:59', mistral: 280, qwen: 220, openai: 180 },
];

const MOCK_TOKEN_DISTRIBUTION: TokenDistribution[] = [
  { name: 'Mistral', value: 40, color: '#f97316' },
  { name: 'Qwen', value: 58, color: '#a855f7' },
  { name: 'OpenAI', value: 23, color: '#10b981' },
];

const ADMIN_CREDENTIALS = {
  email: 'admin@verimark.ai',
  password: 'admin2026',
};

// ─────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ─────────────────────────────────────────────────────────────────────
const getBrandColorClasses = (color: string) => {
  const colors: Record<string, { bg: string; text: string; border: string }> = {
    orange: {
      bg: 'bg-orange-100 dark:bg-orange-900/30',
      text: 'text-orange-700 dark:text-orange-400',
      border: 'border-orange-200 dark:border-orange-800',
    },
    purple: {
      bg: 'bg-purple-100 dark:bg-purple-900/30',
      text: 'text-purple-700 dark:text-purple-400',
      border: 'border-purple-200 dark:border-purple-800',
    },
    green: {
      bg: 'bg-emerald-100 dark:bg-emerald-900/30',
      text: 'text-emerald-700 dark:text-emerald-400',
      border: 'border-emerald-200 dark:border-emerald-800',
    },
  };
  return colors[color] || colors.orange;
};

const formatCurrency = (num: number): string => {
  return `$${num.toLocaleString()}`;
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// ─────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────
const AIProviders: React.FC = () => {
  // ───────────────────────────────────────────────────────────────────
  // STATE
  // ───────────────────────────────────────────────────────────────────
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();

  const [isAdmin, setIsAdmin] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');

  const [activeProvider, setActiveProvider] = useState<string>(MOCK_PROVIDERS[0].id);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showApiKey, setShowApiKey] = useState<Record<string, boolean>>({});
  const [testingConnection, setTestingConnection] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, 'success' | 'error' | null>>({});
  const [showAddKeyModal, setShowAddKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyProvider, setNewKeyProvider] = useState('');
  const [newKeyValue, setNewKeyValue] = useState('');
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);

  // Global settings state
  const [settings, setSettings] = useState<GlobalSettings>({
    defaultProvider: 'mistral',
    offlineMode: false,
    autoFailover: true,
    requestTimeout: 30,
    maxRetryCount: 3,
    rateLimit: 60,
  });

  // ───────────────────────────────────────────────────────────────────
  // EFFECTS
  // ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isLoaded) {
      checkAdminAccess();
    }
  }, [isLoaded, user]);

  useEffect(() => {
    const loadData = async () => {
      if (isAdmin) {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 500));
        setProviders(MOCK_PROVIDERS);
        setApiKeys(MOCK_API_KEYS);
        setIsLoading(false);
      }
    };
    loadData();
  }, [isAdmin]);

  // ───────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────
  const checkAdminAccess = () => {
    const userMetadata = user?.publicMetadata as { role?: string } | undefined;
    if (userMetadata?.role === 'admin') {
      setIsAdmin(true);
      setShowLoginModal(false);
    } else {
      setShowLoginModal(true);
    }
  };

  const handleAdminLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    if (adminEmail === ADMIN_CREDENTIALS.email && adminPassword === ADMIN_CREDENTIALS.password) {
      setIsAdmin(true);
      setShowLoginModal(false);
      setAdminEmail('');
      setAdminPassword('');
    } else {
      setLoginError('Invalid admin credentials');
    }
  };

  const handleLogout = () => {
    setIsAdmin(false);
    setShowLoginModal(true);
  };

  const toggleApiKeyVisibility = (providerId: string) => {
    setShowApiKey(prev => ({
      ...prev,
      [providerId]: !prev[providerId],
    }));
  };

  const testConnection = async (providerId: string) => {
    setTestingConnection(providerId);
    setTestResult(prev => ({ ...prev, [providerId]: null }));

    await new Promise(resolve => setTimeout(resolve, 1500));

    const success = Math.random() > 0.2;
    setTestResult(prev => ({ ...prev, [providerId]: success ? 'success' : 'error' }));
    setTestingConnection(null);

    setProviders(prev => prev.map(p => {
      if (p.id === providerId) {
        return {
          ...p,
          status: success ? 'available' : 'error',
          health: {
            ...p.health,
            responseTime: success ? Math.floor(Math.random() * 200) + 50 : 0,
            successRate: success ? 99.8 : 0,
            lastSuccessfulRequest: success ? new Date().toLocaleString() : '-',
            errorCount: success ? 0 : p.health.errorCount + 1,
          },
        };
      }
      return p;
    }));
  };

  const setAsDefault = (providerId: string) => {
    setProviders(prev => prev.map(p => ({
      ...p,
      isDefault: p.id === providerId,
    })));
    setSettings(prev => ({ ...prev, defaultProvider: providerId }));
  };

  const updateProviderModel = (providerId: string, model: string) => {
    setProviders(prev => prev.map(p =>
      p.id === providerId ? { ...p, currentModel: model } : p
    ));
  };

  const updateProviderApiKey = (providerId: string, apiKey: string) => {
    setProviders(prev => prev.map(p =>
      p.id === providerId ? {
        ...p,
        apiKey,
        status: apiKey ? 'available' : 'not-configured',
      } : p
    ));
  };

  const handleAddApiKey = () => {
    const newKey: APIKey = {
      id: Date.now().toString(),
      name: newKeyName || `Key ${apiKeys.length + 1}`,
      provider: newKeyProvider,
      created: new Date().toISOString().split('T')[0],
      lastUsed: 'Never',
      status: 'active',
      key: newKeyValue || `sk-${newKeyProvider.toLowerCase()}-${Date.now()}`,
    };
    setApiKeys(prev => [...prev, newKey]);
    setShowAddKeyModal(false);
    setNewKeyName('');
    setNewKeyProvider('');
    setNewKeyValue('');
  };

  const revokeApiKey = (keyId: string) => {
    setApiKeys(prev => prev.map(k =>
      k.id === keyId ? { ...k, status: 'revoked' } : k
    ));
  };

  const copyApiKey = (key: string, keyId: string) => {
    navigator.clipboard.writeText(key);
    setCopiedKeyId(keyId);
    setTimeout(() => setCopiedKeyId(null), 2000);
  };

  const updateSetting = <K extends keyof GlobalSettings>(key: K, value: GlobalSettings[K]) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSignOut = () => {
    signOut(() => {
      window.location.href = '/sign-in';
    });
  };

  // ───────────────────────────────────────────────────────────────────
  // RENDER - Admin Login Modal
  // ───────────────────────────────────────────────────────────────────
  if (!isAdmin && showLoginModal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Admin Access Required
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              This page contains sensitive provider configurations.
            </p>
          </div>

          <form onSubmit={handleAdminLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Admin Email
              </label>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder="admin@"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {loginError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{loginError}</p>
              </div>
            )}

            <button
              type="submit"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <Lock className="w-5 h-5" />
              Authenticate
            </button>

            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="w-full px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              Back to Dashboard
            </button>
          </form>

          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-xs text-blue-800 dark:text-blue-300">
              <strong>Demo Credentials:</strong><br />
              Email: admin@*******<br />
              Password: send a request 
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4" />
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2 h-96 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        </div>
      </div>
    );
  }

  // ───────────────────────────────────────────────────────────────────
  // RENDER - Main AI Providers Page
  // ───────────────────────────────────────────────────────────────────
  return (
    <div className="p-8">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">AI Providers</h1>
            <p className="text-gray-600 dark:text-gray-400">
              Manage AI provider connections and configurations
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-lg">
              <Unlock className="w-5 h-5" />
              <span className="text-sm font-medium">Admin Access</span>
            </div>
            
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-sm font-medium">Exit Admin</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column (60%) - Provider Cards + Usage Analytics */}
        <div className="lg:col-span-2 space-y-6">
          {/* Provider Card with Tabs */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            {/* Provider Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
              <nav className="flex gap-6">
                {providers.map((provider) => {
                  const brandColors = getBrandColorClasses(provider.brandColor);
                  return (
                    <button
                      key={provider.id}
                      onClick={() => setActiveProvider(provider.id)}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeProvider === provider.id
                          ? `border-${provider.brandColor}-600 text-${provider.brandColor}-600 dark:text-${provider.brandColor}-400`
                          : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      <div className={`w-8 h-8 ${brandColors.bg} rounded-lg flex items-center justify-center`}>
                        <span className={`text-lg font-bold ${brandColors.text}`}>{provider.logo}</span>
                      </div>
                      {provider.displayName}
                      {provider.isDefault && <Star className="w-4 h-4 text-yellow-600 ml-1" />}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Selected Provider Card Content */}
            {providers.map((provider) => {
              if (provider.id !== activeProvider) return null;
              
              const brandColors = getBrandColorClasses(provider.brandColor);
              const showKey = showApiKey[provider.id] || false;
              const isTesting = testingConnection === provider.id;
              const result = testResult[provider.id];

              return (
                <div key={provider.id}>
                  {/* Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 ${brandColors.bg} rounded-lg flex items-center justify-center`}>
                        <span className={`text-2xl font-bold ${brandColors.text}`}>{provider.logo}</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{provider.displayName}</h3>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${
                              provider.status === 'available' ? 'bg-emerald-500 animate-pulse' :
                              provider.status === 'error' ? 'bg-red-500' :
                              'bg-gray-400'
                            }`} />
                            <span className={`text-sm font-medium ${
                              provider.status === 'available' ? 'text-emerald-600 dark:text-emerald-400' :
                              provider.status === 'error' ? 'text-red-600 dark:text-red-400' :
                              'text-gray-600 dark:text-gray-400'
                            }`}>
                              {provider.status === 'available' ? 'Available' :
                               provider.status === 'error' ? 'Error' : 'Not Configured'}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{provider.description}</p>
                      </div>
                    </div>
                  </div>

                  {/* Configuration */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Current model
                      </label>
                      <select
                        value={provider.currentModel}
                        onChange={(e) => updateProviderModel(provider.id, e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      >
                        {provider.models.map(model => (
                          <option key={model} value={model}>{model}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        API Key
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type={showKey ? 'text' : 'password'}
                          value={provider.apiKey}
                          onChange={(e) => updateProviderApiKey(provider.id, e.target.value)}
                          placeholder="Enter API key"
                          className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          onClick={() => toggleApiKeyVisibility(provider.id)}
                          className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        >
                          {showKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Actions & Stats */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-6">
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Tokens used this month</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">{provider.usage.tokensUsed.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Requests count</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">{provider.usage.requestsCount}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => testConnection(provider.id)}
                        disabled={isTesting || !provider.apiKey}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                          result === 'success' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                          result === 'error' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                          'bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {isTesting ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : result === 'success' ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : result === 'error' ? (
                          <AlertCircle className="w-4 h-4" />
                        ) : (
                          <Activity className="w-4 h-4" />
                        )}
                        Test Connection
                      </button>
                      
                      {!provider.isDefault && (
                        <button
                          onClick={() => setAsDefault(provider.id)}
                          className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg font-medium"
                        >
                          Set as Default
                          <Star className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Provider Health */}
                  {provider.status === 'available' && (
                    <div className={`border-t ${brandColors.border} pt-4`}>
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">PROVIDER HEALTH</h4>
                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Response time</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{provider.health.responseTime}ms</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Success rate</p>
                          <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">{provider.health.successRate}%</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Last successful request</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{provider.health.lastSuccessfulRequest}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Error count</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{provider.health.errorCount}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Usage Analytics */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Usage Analytics</h3>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">API Calls over Time</p>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={MOCK_USAGE_DATA}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="time" stroke="#6b7280" fontSize={12} />
                  <YAxis stroke="#6b7280" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', borderRadius: '8px', border: '1px solid #374151', color: '#fff' }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="mistral" stroke="#f97316" strokeWidth={2} dot={false} name="Mistral" />
                  <Line type="monotone" dataKey="qwen" stroke="#a855f7" strokeWidth={2} dot={false} name="Qwen" />
                  <Line type="monotone" dataKey="openai" stroke="#10b981" strokeWidth={2} dot={false} name="OpenAI" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Token Distribution</p>
                <ResponsiveContainer width="100%" height={150}>
                  <PieChart>
                    <Pie
                      data={MOCK_TOKEN_DISTRIBUTION}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      outerRadius={60}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {MOCK_TOKEN_DISTRIBUTION.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="space-y-3">
                <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-xs text-gray-600 dark:text-gray-400">Monthly Spend Projection</p>
                  <p className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">$3.10 <span className="text-xs text-gray-600 dark:text-gray-400">USD</span></p>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-xs text-gray-600 dark:text-gray-400">Cost Estimator</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">$310.00 <span className="text-xs text-gray-600 dark:text-gray-400">USD</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column (40%) - Global Settings + API Key Management */}
        <div className="space-y-6">
          {/* Global Settings */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Global Settings</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Default AI Provider
                </label>
                <select
                  value={settings.defaultProvider}
                  onChange={(e) => updateSetting('defaultProvider', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                >
                  {providers.map(p => (
                    <option key={p.id} value={p.id}>{p.displayName}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Offline Mode</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Disable all AI requests</p>
                </div>
                <button
                  onClick={() => updateSetting('offlineMode', !settings.offlineMode)}
                  className={`w-11 h-6 rounded-full transition-colors ${
                    settings.offlineMode ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                    settings.offlineMode ? 'translate-x-5' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Auto-failover</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Enable/disable provider switching</p>
                </div>
                <button
                  onClick={() => updateSetting('autoFailover', !settings.autoFailover)}
                  className={`w-11 h-6 rounded-full transition-colors ${
                    settings.autoFailover ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                    settings.autoFailover ? 'translate-x-5' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Request timeout (30s - 300s)
                  </label>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{settings.requestTimeout}s - 300s</span>
                </div>
                <input
                  type="range"
                  min="30"
                  max="300"
                  value={settings.requestTimeout}
                  onChange={(e) => updateSetting('requestTimeout', Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Max retry count (1-10)
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={settings.maxRetryCount}
                  onChange={(e) => updateSetting('maxRetryCount', Number(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rate limiting (requests per minute)
                </label>
                <input
                  type="number"
                  value={settings.rateLimit}
                  onChange={(e) => updateSetting('rateLimit', Number(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* API Key Management */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">API Key Management</h3>
              <button
                onClick={() => setShowAddKeyModal(true)}
                className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Key
              </button>
            </div>

            <div className="space-y-3">
              {apiKeys.map((key) => (
                <div
                  key={key.id}
                  className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{key.name}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{key.provider}</p>
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      key.status === 'active'
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                      {key.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-2">
                    <span>Created: {formatDate(key.created)}</span>
                    <span>Last used: {key.lastUsed}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => copyApiKey(key.key, key.id)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500"
                    >
                      {copiedKeyId === key.id ? (
                        <Check className="w-3 h-3 text-emerald-600" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}
                      {copiedKeyId === key.id ? 'Copied' : 'Copy'}
                    </button>
                    <button
                      onClick={() => revokeApiKey(key.id)}
                      disabled={key.status === 'revoked'}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                    >
                      <Trash2 className="w-3 h-3" />
                      Revoke
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Add API Key Modal */}
      {showAddKeyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAddKeyModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6 mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add New API Key</h3>
              <button
                onClick={() => setShowAddKeyModal(false)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Key Name
                </label>
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., Production Key"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Provider
                </label>
                <select
                  value={newKeyProvider}
                  onChange={(e) => setNewKeyProvider(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select provider</option>
                  {providers.map(p => (
                    <option key={p.id} value={p.displayName}>{p.displayName}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  API Key Value
                </label>
                <input
                  type="password"
                  value={newKeyValue}
                  onChange={(e) => setNewKeyValue(e.target.value)}
                  placeholder="sk-..."
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAddKeyModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleAddApiKey}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white rounded-lg font-medium"
              >
                Add Key
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIProviders;
